// utils/familyChartAdapter.js
// Converts between DB schema (family_members + family_relationships)
// and family-chart's Datum[] format.

/**
 * Convert DB members + relations into family-chart Datum[] format.
 *
 * @param {Array} members       - rows from subject_family table
 * @param {Array} relations     - relationships from subject_family_relationships
 * @param {Object} subjectRecord - the subject record (display_name, born, sex, etc.)
 * @param {string} subjectName  - display name for the subject
 * @returns {Array} family-chart Datum[]
 */
// Map a stored sex value to what family-chart expects. Anything that is not
// clearly M or F becomes '' — the library then renders the card as
// 'card-genderless' (renderers/card-html.ts) using the --genderless-color the
// pane already defines, and buildCardHtml omits the gender pill entirely.
//
// This previously read `=== 'F' ? 'F' : 'M'`, so EVERY person with no recorded
// sex was drawn as a confident "M". On an archive of record, showing an
// asserted fact where none exists is a data-fidelity problem, not a cosmetic
// default.
function toChartGender(sex) {
  const v = String(sex || '').trim().toUpperCase();
  if (v === 'F' || v === 'FEMALE') return 'F';
  if (v === 'M' || v === 'MALE') return 'M';
  return '';
}

export function membersToChartData(members, relations, subjectRecord, subjectName) {
  const data = [];

  // Check if a member with relation="subject" already exists in the DB.
  const subjectMember = members.find(m => m.relation === 'subject');
  const subjectId = subjectMember ? String(subjectMember.id) : 'subject';

  // 1. Create nodes for each member
  members.forEach(m => {
    const id = String(m.id);
    const isSubject = m.relation === 'subject';
    data.push({
      id,
      data: {
        gender: toChartGender(m.sex),
        name: isSubject ? (subjectName || m.name || 'Subject') : (m.name || ''),
        relation: m.relation || 'other',
        birth_year: isSubject ? (subjectRecord?.born || m.birth_year || '') : (m.birth_year || ''),
        death_year: isSubject ? (subjectRecord?.death_year || m.death_year || '') : (m.death_year || ''),
        birth_place: m.birth_place || '',
        death_place: m.death_place || '',
        married_name: m.married_name || '',
        fate: isSubject ? 'survived' : (m.fate || 'unknown'),
        fate_detail: m.fate_detail || '',
        notes: m.notes || '',
        source: m.source || '',
        source_locator: m.source_locator || '',
        _isSubject: isSubject,
      },
      rels: {
        parents: [],
        spouses: [],
        children: [],
      },
    });
  });

  // If no subject member exists in the DB, create a synthetic subject node
  if (!subjectMember) {
    const subjectGender = toChartGender(subjectRecord?.sex);
    data.unshift({
      id: 'subject',
      data: {
        gender: subjectGender,
        name: subjectName || 'Subject',
        relation: 'subject',
        birth_year: subjectRecord?.born || '',
        death_year: subjectRecord?.death_year || '',
        fate: 'survived',
        _isSubject: true,
      },
      rels: {
        parents: [],
        spouses: [],
        children: [],
      },
    });
  }

  // Build a quick lookup
  const byId = {};
  data.forEach(d => { byId[d.id] = d; });

  // Also map the string 'subject' to the actual subject node
  if (subjectMember && !byId['subject']) {
    byId['subject'] = byId[subjectId];
  }

  // Wire relationships into the chart nodes
  const allRels = (relations && relations.length > 0) ? relations : [];
  allRels.forEach(rel => {
    const a = String(rel.partnerA || '');
    const b = String(rel.partnerB || '');
    const datumA = byId[a];
    const datumB = byId[b];
    const idA = datumA ? datumA.id : a;
    const idB = datumB ? datumB.id : b;

    if (datumA && datumB) {
      if (!datumA.rels.spouses.includes(idB)) datumA.rels.spouses.push(idB);
      if (!datumB.rels.spouses.includes(idA)) datumB.rels.spouses.push(idA);
    }

    (rel.children || []).forEach(childId => {
      const cid = String(childId);
      const childDatum = byId[cid];
      if (!childDatum) return;
      // family-chart enforces max 2 parents per child — skip if already full
      if (childDatum.rels.parents.length >= 2) return;
      if (datumA && !childDatum.rels.parents.includes(idA) && childDatum.rels.parents.length < 2) {
        childDatum.rels.parents.push(idA);
        if (!datumA.rels.children.includes(childDatum.id)) datumA.rels.children.push(childDatum.id);
      }
      if (datumB && !childDatum.rels.parents.includes(idB) && childDatum.rels.parents.length < 2) {
        childDatum.rels.parents.push(idB);
        if (!datumB.rels.children.includes(childDatum.id)) datumB.rels.children.push(childDatum.id);
      }
    });
  });

  // ── Strict validation for family-chart ──
  // family-chart's createRelsToAdd requires every child to have exactly 1 or 2 parents.
  // Rebuild all parent↔child links from scratch to ensure consistency.
  // 1. Collect all claimed parent→child edges
  const parentChildEdges = []; // [{parentId, childId}]
  data.forEach(d => {
    (d.rels.children || []).forEach(cid => {
      if (byId[cid]) parentChildEdges.push({ parentId: d.id, childId: cid });
    });
  });

  // 2. Clear all parent/child arrays
  data.forEach(d => { d.rels.parents = []; d.rels.children = []; });

  // 3. Re-add edges, enforcing max 2 parents per child
  const childParentCount = {}; // childId → number of parents assigned
  parentChildEdges.forEach(({ parentId, childId }) => {
    const count = childParentCount[childId] || 0;
    if (count >= 2) return; // already has 2 parents — skip
    const parent = byId[parentId];
    const child = byId[childId];
    if (!parent || !child) return;
    parent.rels.children.push(childId);
    child.rels.parents.push(parentId);
    childParentCount[childId] = count + 1;
  });

  // Ensure the subject node is first in the array (family-chart uses data[0] as default main)
  const subjectIdx = data.findIndex(d => d.data._isSubject);
  if (subjectIdx > 0) {
    const [subj] = data.splice(subjectIdx, 1);
    data.unshift(subj);
  }

  return data;
}


/**
 * Infer relationships from member `relation` fields and return them
 * in the same format as the relations state (partnerA, partnerB, children, etc.).
 * Called when members exist but no explicit relationships are stored.
 *
 * CRITICAL CONSTRAINT: Each member ID can appear as a child in at most ONE
 * relationship. family-chart throws "child has more than 1 parent" otherwise.
 *
 * @param {Array} members  - rows from subject_family table
 * @returns {Array} relationships in {id, partnerA, partnerB, children, label, sortOrder} format
 */
export function inferRelationshipsFromMembers(members) {
  if (!members || members.length === 0) return [];

  const subjectMember = members.find(m => m.relation === 'subject');
  const subjectId = subjectMember ? String(subjectMember.id) : 'subject';

  // Collect members by relation type
  const byRelation = {};
  members.forEach(m => {
    const rel = m.relation || 'other';
    if (!byRelation[rel]) byRelation[rel] = [];
    byRelation[rel].push(m);
  });

  const id = m => String(m.id);
  const fathers = byRelation['father'] || [];
  const mothers = byRelation['mother'] || [];
  const grandfathers = byRelation['grandfather'] || [];
  const grandmothers = byRelation['grandmother'] || [];
  const spouses = byRelation['spouse'] || [];
  const siblings = byRelation['sibling'] || [];
  const children = byRelation['child'] || [];
  const grandchildren = [
    ...(byRelation['grandchild'] || []),
    ...(byRelation['grandson'] || []),
    ...(byRelation['granddaughter'] || []),
  ];
  const sonsInLaw = byRelation['son_in_law'] || [];
  const daughtersInLaw = byRelation['daughter_in_law'] || [];
  const inLaws = [...sonsInLaw, ...daughtersInLaw];
  const uncles = byRelation['uncle'] || [];
  const aunts = byRelation['aunt'] || [];
  const cousins = byRelation['cousin'] || [];
  const nephews = byRelation['nephew'] || [];
  const nieces = byRelation['niece'] || [];
  const brothersInLaw = byRelation['brother_in_law'] || [];
  const sistersInLaw = byRelation['sister_in_law'] || [];

  // Track which member IDs are already assigned as a child in some relationship
  const assignedAsChild = new Set();
  const relationships = [];
  let idx = 0;

  // Only add childIds that haven't been assigned yet
  function safeChildIds(ids) {
    return ids.filter(cid => !assignedAsChild.has(cid));
  }

  function addRel(partnerA, partnerB, childIds) {
    const safe = safeChildIds(childIds);
    safe.forEach(cid => assignedAsChild.add(cid));
    relationships.push({
      id: `inferred-${idx++}`,
      partnerA,
      partnerB,
      children: safe,
      label: '',
      // Marks this as GUESSED, not recorded from the testimony. Persisted to
      // the DB's `source` column so the distinction survives outside the UI —
      // an id prefix alone is invisible to exports and queries. Cleared to
      // 'manual' the moment a human edits the relationship to confirm it.
      source: 'inferred',
      sortOrder: relationships.length,
    });
  }

  // ── 1. Subject's parents → subject + siblings ──
  const father = fathers[0];
  const mother = mothers[0];
  if (father && mother) {
    addRel(id(father), id(mother), [subjectId, ...siblings.map(id)]);
  } else if (father) {
    addRel(id(father), '', [subjectId, ...siblings.map(id)]);
  } else if (mother) {
    addRel(id(mother), '', [subjectId, ...siblings.map(id)]);
  }

  // ── 2. Paternal grandparents → father + uncles/aunts ──
  if (grandfathers.length > 0 || grandmothers.length > 0) {
    const pgf = grandfathers[0];
    const pgm = grandmothers[0];
    const fatherChildren = [
      ...(father ? [id(father)] : []),
      ...uncles.map(id),
      ...aunts.map(id),
    ];
    if (pgf && pgm) {
      addRel(id(pgf), id(pgm), fatherChildren);
    } else if (pgf) {
      addRel(id(pgf), '', fatherChildren);
    } else if (pgm) {
      addRel(id(pgm), '', fatherChildren);
    }

    // Maternal grandparents (2nd pair) → mother
    const mgf = grandfathers[1];
    const mgm = grandmothers[1];
    if (mgf || mgm) {
      const motherChildren = mother ? [id(mother)] : [];
      if (mgf && mgm) {
        addRel(id(mgf), id(mgm), motherChildren);
      } else if (mgf) {
        addRel(id(mgf), '', motherChildren);
      } else if (mgm) {
        addRel(id(mgm), '', motherChildren);
      }
    }
  }

  // ── 3. Subject + spouse(s) → children ──
  if (spouses.length > 0) {
    addRel(subjectId, id(spouses[0]), children.map(id));
    for (let i = 1; i < spouses.length; i++) {
      addRel(subjectId, id(spouses[i]), []);
    }
  } else if (children.length > 0) {
    addRel(subjectId, '', children.map(id));
  }

  // ── 4. In-laws paired with children → grandchildren ──
  const childrenById = new Map();
  children.forEach(c => childrenById.set(id(c), c));

  // Pair in-laws with children
  const pairedChild = new Map(); // childId → in-law couple relationship index
  inLaws.forEach(il => {
    // Find the first unmatched child
    const unmatched = children.find(ch => !pairedChild.has(id(ch)));
    if (unmatched) {
      const relIdx = relationships.length;
      addRel(id(unmatched), id(il), []);
      pairedChild.set(id(unmatched), relIdx);
    }
  });

  // Distribute grandchildren among children
  if (grandchildren.length > 0 && children.length > 0) {
    grandchildren.forEach((gc, i) => {
      if (assignedAsChild.has(id(gc))) return;
      const parentChild = children[i % children.length];
      const pairRelIdx = pairedChild.get(id(parentChild));
      if (pairRelIdx !== undefined && relationships[pairRelIdx]) {
        // Add to the child+in-law couple
        relationships[pairRelIdx].children.push(id(gc));
        assignedAsChild.add(id(gc));
      } else {
        // Create a new single-parent relationship
        addRel(id(parentChild), '', [id(gc)]);
      }
    });
  }

  // ── 5. Cousins → children of uncles/aunts ──
  const unclesAndAunts = [...uncles, ...aunts];
  if (cousins.length > 0 && unclesAndAunts.length > 0) {
    // Group cousins by their assigned uncle/aunt
    const uaGroups = new Map();
    cousins.forEach((c, i) => {
      if (assignedAsChild.has(id(c))) return;
      const ua = unclesAndAunts[i % unclesAndAunts.length];
      if (!uaGroups.has(id(ua))) uaGroups.set(id(ua), []);
      uaGroups.get(id(ua)).push(id(c));
    });
    uaGroups.forEach((cids, uaId) => {
      addRel(uaId, '', cids);
    });
  }

  // ── 6. Nephews/nieces → children of siblings ──
  const allNephewNiece = [...nephews, ...nieces];
  if (allNephewNiece.length > 0 && siblings.length > 0) {
    const sibGroups = new Map();
    allNephewNiece.forEach((nn, i) => {
      if (assignedAsChild.has(id(nn))) return;
      const sib = siblings[i % siblings.length];
      if (!sibGroups.has(id(sib))) sibGroups.set(id(sib), []);
      sibGroups.get(id(sib)).push(id(nn));
    });
    sibGroups.forEach((cids, sibId) => {
      addRel(sibId, '', cids);
    });
  }

  // ── 7. Brothers/sisters-in-law → attach as spouse-siblings ──
  // These are siblings of the subject's spouse.
  // We can't easily place them in a tree without knowing the spouse's parents,
  // so attach them as children of a virtual "spouse's parents" couple.
  // For simplicity, just ensure they're connected somewhere.
  const sibsInLaw = [...brothersInLaw, ...sistersInLaw];
  // (handled below in unconnected fallback if not already placed)

  // ── 8. Unconnected members → attach to subject ──
  const connected = new Set();
  connected.add(subjectId);
  relationships.forEach(r => {
    if (r.partnerA) connected.add(r.partnerA);
    if (r.partnerB) connected.add(r.partnerB);
    r.children.forEach(c => connected.add(c));
  });

  const unconnected = members
    .filter(m => m.relation !== 'subject' && !connected.has(String(m.id)))
    .map(m => String(m.id));

  if (unconnected.length > 0) {
    // Find existing subject couple to attach unconnected members as children
    const subjectCouple = relationships.find(r =>
      (r.partnerA === subjectId || r.partnerB === subjectId) &&
      r.children.length >= 0
    );
    if (subjectCouple) {
      const safe = safeChildIds(unconnected);
      safe.forEach(cid => {
        assignedAsChild.add(cid);
        subjectCouple.children.push(cid);
      });
    } else {
      addRel(subjectId, '', unconnected);
    }
  }

  // Filter out empty relationships (no partner B and no children)
  return relationships.filter(r => r.partnerA && (r.partnerB || r.children.length > 0));
}
